<template>
    <div v-if="actionProgressStatusIs('skipped')">
        <div class="action-completed">
            <p>{{ uLang('app.action_has_been_skipped') }}</p>
        </div>
    </div>
    <div v-else-if="actionProgressStatusIs('canceled')">
        <div class="action-completed">
            <h3>{{ uLang('app.action_canceled') }}</h3>
            <p>{{ uLang('app.action_canceled_text')}}</p>
        </div>
    </div>
    <div v-else>
        <template v-if="categoryIs(/opdrachtovereenkomst.*/)">
            <div v-if="actionProgressStatusIs('started')">
                <div v-show="!showDenyInput">
                    <button
                        role="button"
                        class="btn btn-danger"
                        :disabled="working"
                        @click.prevent="toggleDenyInput"
                    >
                        <i class="fa fa-thumbs-down" aria-hidden="true"></i> {{ uLang('app.reject') }}
                    </button>
                    <button
                        role="button"
                        class="btn btn-success ml-4"
                        :disabled="working"
                        @click.prevent="accept"
                    >
                        <i class="fa fa-thumbs-up" aria-hidden="true"></i> {{ uLang('app.approve') }}
                    </button>
                </div>
                <div v-show="showDenyInput">
                    <div class="form-group" :class="{ 'has-danger': hasDenyError }">
                        <label for="deny-input" v-text="uLang('app.deny_terms_reason')"></label>
                        <textarea id="deny-input" v-model="denyTermsReason" class="form-control my-1" required></textarea>
                    </div>
                    <div class="mt-4">
                        <button
                            role="button"
                            class="btn btn-default"
                            :disabled="working"
                            @click.prevent="toggleDenyInput"
                            v-show="showDenyInput"
                        >
                            <i class="fa fa-times" aria-hidden="true"></i> {{ uLang('app.cancel') }}
                        </button>
                        <button
                            role="button"
                            class="btn btn-danger ml-4"
                            :disabled="working"
                            @click.prevent="deny"
                            v-show="showDenyInput"
                        >
                            <i class="fa fa-thumbs-down" aria-hidden="true"></i> {{ uLang('app.reject') }}
                        </button>
                    </div>
                </div>
            </div>
            <div v-else-if="actionProgressStatusIs('completed')" class="action-completed">
                <p>{{ uLang('app.coach_accepted_terms')}}</p>
            </div>
            <template v-else></template>
        </template>
        <template v-else-if="categoryIs(/correspondentie \(via mail\)/)">
            <div v-if="actionProgressStatusIs('started')">
                <button
                    role="button"
                    class="btn btn-success"
                    :disabled="working"
                    @click.prevent="mailSave"
                >
                    <i class="fa fa-save mr-2" aria-hidden="true"></i> {{ uLang('app.save') }}
                </button>
                <button
                    role="button"
                    class="btn btn-success"
                    :disabled="working || !canMail"
                    @click.prevent="mailSubmit"
                >
                    <i class="fa fa-envelope-o mr-2" aria-hidden="true"></i> {{ uLang('app.submit_and_mail') }}
                </button>
            </div>
            <div v-else-if="actionProgressStatusIs('completed')" class="action-completed">
                <p>{{ uLang('app.action_completed_mail_sent')}}</p>
            </div>
        </template>
        <template v-else>
            <a class="btn btn-brand" @click.prevent="save">
                {{ uLang('app.save') }}
            </a>
            <a class="btn btn-brand" 
                :class="{ 'disabled': fillingError !== '' }"
                @click.prevent="handleSubmit">
                {{ uLang('app.save_and_submit') }}
            </a>
            <span class="nearbutton-style">{{ fillingError }}</span>
        </template>
    </div>
</template>

<script>
    import { ConfirmationAlert } from '~/Swals';

    export default {
        name: 'BookBuyInvoiceButton',

        props: {
            data: {
                type: Object,
            },
            actionProgress: {
                type: Object,
            },
            working: {
                type: Boolean,
                default: false,
            },
            canMail: {
                type: Boolean,
                default: false,
            },
            errors: {
                type: Object,
                default: () => {},
            },
            fillingError: {
                type: String,
            }
        },

        data() {
            return {
                showDenyInput: false,
                denyTermsReason: ''
            }
        },

        computed: {
            hasDenyError() {
                return this.errors.hasOwnProperty('deny_terms_reason');
            }
        },

        methods: {

            handleSubmit() {
                if (this.fillingError !== '') {
                    return;
                }
                this.submit();
            },

            /**
             * Helper to check the status name of the action progress.
             */
            actionProgressStatusIs(name) {
                return $getIfExists(this.actionProgress, 'action_progress_status.name') === name;
            },

            /**
             * Helper to check the status name of the category (of the ActionDocumentVersion).
             */
            categoryIs(regex) {
                return $getIfExists(this.data, 'action_document_version.action_document.action_document_category.name')
                    .match(regex)
            },

            /**
             * Whether or not we show the deny input
             */
            toggleDenyInput() {
                this.showDenyInput = !this.showDenyInput;
            },

            /**
             * For accepting the terms stated in this document.
             */
            accept() {
                this.$emit('submit', {'submitType': 'accept', 'successText': 'choice_saved'});
            },

            /**
             * For denying the terms stated in this document.
             */
            deny() {
                new ConfirmationAlert({
                    text: this.uLang('app.deny_terms_question')
                })
                    .show()
                    .then(() => {
                        this.$emit('submit', {'submitType': 'deny', 'successText': 'choice_saved', 'options': { 'deny_terms_reason': this.denyTermsReason }});
                    });
            },

            /**
             * Save all the form data, do not send it in for checking.
             */
            save() {
                this.$emit('submit', {'submitType': 'save', 'successText': 'submission_sent'});
            },

            /**
             * Save all the form data, send it to the trajectory administrator for checking.
             */
            submit() {
                this.$emit('submit', {'submitType': 'submit', 'successText': 'submission_sent'});
            },

            /**
             * Save the fields available in the mail template.
             */
            mailSave() {
                this.$emit('submit', {'submitType': 'save', 'successText': 'successfully_saved'});
            },

            /**
             * Save the fields available, and actually send out the mail to the candidate.
             */
            mailSubmit() {
                new ConfirmationAlert({
                    text: this.uLang('app.submit_and_mail_question')
                })
                    .show()
                    .then(() => {
                        this.$emit('submit', {'submitType': 'submit_and_mail', 'successText': 'appointment_confirmation_sent'});
                    })
            },
        },
    }
</script>

<style scoped>
a.disabled {
    pointer-events: none;
    opacity: 0.6;
    cursor: not-allowed;
}
span.nearbutton-style 
{
    color:red;
    margin-left:10px;
}
</style>
